import { SalesOrderPermissions } from '@order-management/roles';
import {
  BasicSalesOrderData,
  DeferredResponse,
  HomeSections,
  MySalesOrdersCriteria,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigation,
} from 'react-router-dom';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import HomeSectionsHeaderTabs from '../../shared/home-sections-header-tabs/home-sections-header-tabs';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsPartnerUser from '../../shared/hooks/is-partner-user';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import TableForm from '../../ui/table/table-form/table-form';
import { fetchMySalesOrders } from '../../users/users-service';
import SalesOrderSummaryList from '../sales-order-summary-list/sales-order-summary-list';

const FETCH_MY_SALES_ORDERS_ERROR_MESSAGE =
  'My Sales Orders could not be fetched. Please try again';
const EMPTY_SALES_ORDERS_MESSAGE = 'There are no sales orders at the moment';

const initialCriteria: MySalesOrdersCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

export const loader: LoaderFunction = async () => {
  const response = fetchMySalesOrders(initialCriteria);
  return defer({ response });
};

export function MySalesOrdersPageContent() {
  const navigation = useNavigation();
  const isPartnerUser = useIsPartnerUser();
  const mySalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<BasicSalesOrderData>;
  const [mySalesOrders, setMySalesOrders] = useState<
    PaginatedAPIResponse<BasicSalesOrderData>
  >(mySalesOrdersInitialResponse);
  const [criteria, setCriteria] =
    useState<MySalesOrdersCriteria>(initialCriteria);
  const { executeHttpRequest, isLoading: isLoadingSalesOrders } =
    useHttpRequest();

  const listConfig: PaginatedContentConfig = useMemo(
    () => ({ ...criteria, pages: mySalesOrders.pages }),
    [criteria, mySalesOrders.pages]
  );

  const isPageDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  const fetchRelatedSalesOrders = useCallback(
    (criteria: MySalesOrdersCriteria) =>
      executeHttpRequest({
        asyncFunction: async () => {
          const relatedSalesOrdersResponse = await fetchMySalesOrders(criteria);
          setMySalesOrders(relatedSalesOrdersResponse);
        },
        customErrorMessage: FETCH_MY_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeHttpRequest]
  );

  const handleOnChange = useCallback(
    (value: PaginatedContentConfig): void => {
      const newCriteria = {
        ...criteria,
        ...value,
      };

      setCriteria(newCriteria);
      fetchRelatedSalesOrders(newCriteria);
    },
    [criteria, fetchRelatedSalesOrders]
  );

  return (
    <BoxLayout
      className={`is-min-height-80 mx-10 ${isPartnerUser ? 'my-4' : 'mb-4'}`}
      headerClassName={isPartnerUser ? '' : 'is-radiusless'}
      header={
        <Columns marginless>
          <Columns.Column>
            <h1 className="is-size-3 has-text-weight-light">My Sales Orders</h1>
            <h2 className="is-size-5 has-text-weight-light">
              All Sales Orders (SOs) assigned to or shared with you.
            </h2>
          </Columns.Column>
        </Columns>
      }
    >
      <Conditional condition={isPageDataLoading}>
        <Conditional.True>
          <PageSpinner />
        </Conditional.True>
        <Conditional.False>
          <TableForm
            config={listConfig}
            enableSearch={false}
            onChange={handleOnChange}
          />
          <Conditional condition={isLoadingSalesOrders}>
            <Conditional.True>
              <PageSpinner />
            </Conditional.True>
            <Conditional.False>
              <Conditional condition={mySalesOrders.data.length > 0}>
                <Conditional.True>
                  <SalesOrderSummaryList salesOrders={mySalesOrders.data} />
                </Conditional.True>
                <Conditional.False>
                  <Element
                    display="flex"
                    justifyContent="center"
                    className="my-2"
                  >
                    <span className="has-text-weight-light">
                      {EMPTY_SALES_ORDERS_MESSAGE}
                    </span>
                  </Element>
                </Conditional.False>
              </Conditional>
            </Conditional.False>
          </Conditional>
        </Conditional.False>
      </Conditional>
    </BoxLayout>
  );
}

export function MySalesOrdersPage() {
  const { response } = useLoaderData() as DeferredResponse<
    PaginatedAPIResponse<BasicSalesOrderData>
  >;
  const isPartnerUser = useIsPartnerUser();

  const Skeleton = () => (
    <BoxLayout
      className={`is-min-height-80 mx-10 ${isPartnerUser ? 'my-4' : 'mb-4'}`}
      headerClassName={isPartnerUser ? '' : 'is-radiusless'}
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <h1 className="is-size-3 has-text-weight-light">My Sales Orders</h1>
            <h2 className="is-size-5 has-text-weight-light">
              All Sales Orders (SOs) assigned to or shared with you. Accept any
              outstanding End User License Agreements to resolve any open
              actions.
            </h2>
            <CgSpinner className="animate-spin" />
          </Columns.Column>
        </Columns>
      }
    >
      <PageSpinner />
    </BoxLayout>
  );

  return (
    <ProtectedPage
      permissions={[SalesOrderPermissions.SALES_ORDERS_SUMMARY_READ]}
    >
      <If condition={!isPartnerUser}>
        <HomeSectionsHeaderTabs activeTab={HomeSections.SALES_ORDERS} />
      </If>
      <Suspense fallback={<Skeleton />}>
        <ErrorHandlingAwait resolve={response}>
          <MySalesOrdersPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default MySalesOrdersPage;
